import getEnvironment from '@/utils/environment';

import { LOCAL_STORAGE_ACCESS_TOKEN_KEY, LOCAL_STORAGE_REFRESH_TOKEN_KEY } from '../constants/api';

const refreshTokenAndRetry = (baseRoute: string) => {
  const refreshToken = localStorage.getItem(LOCAL_STORAGE_REFRESH_TOKEN_KEY);

  if (refreshToken) {
    return fetch(`${baseRoute}/auth/refresh-token`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${refreshToken}`,
      },
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.statusText);
        }
        return response.json();
      })
      .then((tokens) => {
        localStorage.setItem(LOCAL_STORAGE_ACCESS_TOKEN_KEY, tokens.accessToken);
        localStorage.setItem(LOCAL_STORAGE_REFRESH_TOKEN_KEY, tokens.refreshToken);
        return true;
      })
      .catch(() => false);
  }

  return false;
};

const apiFetch = (url: string, options: RequestInit = {}, retryOnUnauthorized = true) => {
  const { headers: optionHeaders, ...restOptions } = options;
  const defaultOptions: RequestInit = {
    ...restOptions,
    // Merge caller headers on top of the defaults so per-request headers (e.g. `Language`)
    // don't wipe out `Content-Type`/`Authorization`.
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem(LOCAL_STORAGE_ACCESS_TOKEN_KEY)}`,
      ...optionHeaders,
    },
  };

  const baseRoute = getEnvironment().apiUrl;

  const fetchWithRetry = async (): Promise<any> => {
    const response = await fetch(
      `${baseRoute}/${url.startsWith('/') ? url.slice(1) : url}`,
      defaultOptions,
    );
    if (!response.ok) {
      if (response.status === 401 && retryOnUnauthorized) {
        const refreshed = await refreshTokenAndRetry(baseRoute);
        if (refreshed) return apiFetch(url, options, false);
      }
      // Best-effort capture of the error body so downstream consumers can read structured details
      // (e.g. backend `{ message, statusCode, ... }`) without breaking existing `response.message` users.
      let errorBody: unknown;
      try {
        const errContentType = response.headers.get('Content-Type') || '';
        if (errContentType.includes('application/json')) {
          errorBody = await response.json();
        } else if (errContentType.includes('text/')) {
          errorBody = await response.text();
        }
      } catch {
        errorBody = undefined;
      }
      const exception = new Error(response.statusText);
      (exception as any).response = {
        status: response.status,
        message: response.statusText,
        data: errorBody,
      };
      throw exception;
    }

    const contentType = response.headers.get('Content-Type');

    if (contentType?.includes('application/json')) return response.json();
    if (contentType?.includes('text/')) return response.text();
    return response;
  };

  return fetchWithRetry();
};

export const apiGet = (url: string, options: RequestInit = {}) =>
  apiFetch(url, { ...options, method: 'GET' });
export const apiPost = (url: string, body: any, options: RequestInit = {}) =>
  apiFetch(url, { ...options, method: 'POST', body: JSON.stringify(body) });
export const apiPut = (url: string, body: any, options: RequestInit = {}) =>
  apiFetch(url, { ...options, method: 'PUT', body: JSON.stringify(body) });
export const apiDelete = (url: string, body?: any, options: RequestInit = {}) =>
  apiFetch(url, { ...options, method: 'DELETE', body: body ? JSON.stringify(body) : undefined });

export const apiClient = {
  get: apiGet,
  post: apiPost,
  put: apiPut,
  delete: apiDelete,
};
